import { isLiveDashboardChart } from './liveDashboardDefaults'
import {
  buildGroupedHierarchy,
  createNameMatcher,
  flattenDimensionAttributes,
  parseGroupedSelectionToUiState,
  resolveGroupedAttributeIds,
  stripParentIdsWhenChildrenPresent,
} from './liveDashboardDimensionUtils'

/** Dashboard region-to-country groupings for the market snapshot filter. */
export const LIVE_DASHBOARD_GEOGRAPHY_GROUPS = [
  {
    region: 'North America',
    countries: ['Canada', 'United States'],
  },
  {
    region: 'LATAM',
    countries: ['Brazil', 'Chile', 'Mexico'],
  },
  {
    region: 'Western Europe',
    countries: [
      'Austria',
      'France',
      'Germany',
      'Italy',
      'Netherlands',
      'Denmark',
      'Finland',
      'Norway',
      'Sweden',
      'Spain',
      'Switzerland',
      'United Kingdom',
    ],
  },
  {
    region: 'Middle East & Africa',
    countries: ['GCC Region'],
  },
  {
    region: 'Asia-Pacific',
    countries: [
      'China',
      'Hong Kong',
      'Japan',
      'South Korea',
      'Taiwan',
      'Indonesia',
      'Malaysia',
      'Singapore',
      'Thailand',
      'India',
      'Australia',
    ],
  },
]

export const LIVE_DASHBOARD_REGION_NAMES = LIVE_DASHBOARD_GEOGRAPHY_GROUPS.map((group) => group.region)

export const LIVE_DASHBOARD_COUNTRY_NAMES = LIVE_DASHBOARD_GEOGRAPHY_GROUPS.flatMap(
  (group) => group.countries,
)

const geographyNameMatcher = createNameMatcher()
const {
  findMatchingAllowedName,
  findAttributeByAllowedName,
  findDashboardParentAttribute,
} = geographyNameMatcher

const buildHierarchyFromMetadata = (dimension) => {
  const flatAttributes = flattenDimensionAttributes(dimension?.Attributes)
  if (!flatAttributes.length) {
    return { regionsWithCountries: [], regionOnlyNodes: [] }
  }

  const { groupsWithChildren, leafOnlyGroups } = buildGroupedHierarchy(
    flatAttributes,
    LIVE_DASHBOARD_GEOGRAPHY_GROUPS,
    {
      getParentName: (group) => group.region,
      getChildNames: (group) => group.countries,
      allChildNames: LIVE_DASHBOARD_COUNTRY_NAMES,
      findDashboardParentAttribute,
      findAttributeByAllowedName,
    },
  )

  return {
    regionsWithCountries: groupsWithChildren.map((group) => ({
      id: group.id,
      name: group.name,
      countries: group.children,
    })),
    regionOnlyNodes: leafOnlyGroups,
  }
}

export const findDefaultGeographyAttributeId = (dimension, preferredCountryName = 'France') => {
  const hierarchy = buildGeographyHierarchy(dimension)

  for (const region of hierarchy.regionsWithCountries) {
    const preferredCountry = region.countries.find(
      (country) => findMatchingAllowedName(country.name, [preferredCountryName]) != null,
    )
    if (preferredCountry) {
      return preferredCountry.id
    }
  }

  return hierarchy.regionsWithCountries[0]?.countries[0]?.id
    ?? hierarchy.regionOnlyNodes[0]?.id
    ?? null
}

export const findAllDefaultGeographyAttributeIds = (dimension) => {
  const hierarchy = buildGeographyHierarchy(dimension)
  const selectedRegionIds = [
    ...hierarchy.regionsWithCountries.map((region) => region.id),
    ...hierarchy.regionOnlyNodes.map((region) => region.id),
  ]
  const selectedCountryIds = hierarchy.regionsWithCountries.flatMap((region) =>
    region.countries.map((country) => country.id),
  )

  return resolveGeographyApiAttributeIds(selectedRegionIds, selectedCountryIds, hierarchy)
}

export function buildGeographyHierarchy(dimension) {
  return buildHierarchyFromMetadata(dimension)
}

export function isGeographyDimension(dimension) {
  const name = String(dimension?.Name ?? '').toLowerCase()
  const display = String(dimension?.DisplayName ?? '').toLowerCase()
  return name === 'countryid' || name === 'geographyid' || display.includes('geograph')
}

export function isLiveDashboardGeographyDimension(chart, dimension) {
  return isLiveDashboardChart(chart) && isGeographyDimension(dimension)
}

export function parseGeographySelectionToUiState(attributeIds, hierarchy) {
  const uiState = parseGroupedSelectionToUiState(attributeIds, hierarchy, {
    groupsWithChildren: hierarchy.regionsWithCountries.map((region) => ({
      id: region.id,
      children: region.countries,
    })),
    leafOnlyGroups: hierarchy.regionOnlyNodes,
  })

  return {
    selectedRegionIds: uiState.selectedParentIds,
    selectedCountryIds: uiState.selectedChildIds,
  }
}

export function resolveGeographyApiAttributeIds(selectedRegionIds, selectedCountryIds, hierarchy) {
  return resolveGroupedAttributeIds(selectedRegionIds, selectedCountryIds, hierarchy, {
    groupsWithChildren: hierarchy.regionsWithCountries.map((region) => ({
      id: region.id,
      children: region.countries,
    })),
    leafOnlyGroups: hierarchy.regionOnlyNodes,
  })
}

export function applyLiveDashboardGeographyResolutionToSelections(selections, dimensions) {
  if (!Array.isArray(selections) || !Array.isArray(dimensions)) {
    return selections
  }

  return selections.map((selection) => {
    const dimension = dimensions.find((entry) => entry.DimensionID === selection.DimensionID)
    if (!dimension || !isGeographyDimension(dimension)) {
      return selection
    }

    const hierarchy = buildGeographyHierarchy(dimension)
    const uiState = parseGeographySelectionToUiState(selection.Attributes, hierarchy)
    const resolvedAttributes = stripParentIdsWhenChildrenPresent(
      resolveGeographyApiAttributeIds(
        uiState.selectedRegionIds,
        uiState.selectedCountryIds,
        hierarchy,
      ),
      hierarchy.regionsWithCountries.map((region) => ({
        id: region.id,
        children: region.countries,
      })),
    )

    return {
      ...selection,
      Attributes: resolvedAttributes,
    }
  })
}
